import { take, select, put, delay, race, takeLatest } from 'redux-saga/effects';
import { gameDataActions, GAME_DATA_ACTIONS } from '../Actions/gameDataActions';
import { heroActions } from '../Actions/heroActions';
import { animateActions } from '../Actions/animateActions';
import { logsActions } from '../Actions/logsActions';
import { getCurrentMonstersIndex, getFightDataMonsters, getHero, getHeroName } from '../Selectors/selectors';
import { getSpecialAttack } from '../../Helpers/heroAbilities';
import { getEffectiveStats } from '../../Helpers/equipment';
import {
    MANA_COST,
    hasEnoughMana,
    calculateBasicDamage,
    calculateStunDamage,
    calculateArmorPenDamage,
} from '../../Helpers/damage';
import { log } from "../../Helpers/logger";

// Consommer du mana pour une attaque
function* consumeMana(hero, isSpecialAttack = false) {
    const manaCost = isSpecialAttack ? MANA_COST.SPECIAL_ATTACK : MANA_COST.BASIC_ATTACK;
    
    // Utiliser l'action consumeMana pour déclencher la logique de consommation de mana
    yield put(heroActions.consumeMana(manaCost));
    
    // Retourner le nouveau montant de mana (actuel - coût)
    return Math.max(0, (hero.stats.mana || 0) - manaCost);
}

// Régénérer du mana (appelé lors des déplacements)
function* regenMana(hero) {
    if (!hero?.stats?.maxMana) return;
    
    const regenAmount = Math.ceil(hero.stats.maxMana * MANA_COST.MOVE_REGEN);
    const currentMana = hero.stats.mana || 0;
    const newMana = Math.min(hero.stats.maxMana, currentMana + regenAmount);
    
    if (newMana > currentMana) {
        // Utiliser l'action castSpell avec un coût négatif pour régénérer du mana
        yield put(heroActions.castSpell('mana_regen', -regenAmount));
        
        if (regenAmount > 0) {
            yield put(logsActions.add(`Vous récupérez ${regenAmount} points de mana en vous déplaçant.`));
        }
    }
    
    return newMana;
}

export const FIGHT_SAGA_RESULTS = {
    PLAYER_VICTORY: "player victory",
    PLAYER_DEAD: "player dead",
    PLAYER_ESCAPE: "player escape"
}

const logDelay = 500;

// const attackData = {
//     target: 'single',
//     type: 'physical',
//     damage: 5,
// }

// Watcher pour la boucle de combat
export function* watchFightSaga() {
    yield takeLatest('FIGHT_LOOP', fightSaga);
    yield takeLatest('FIGHT_SPECIAL_ATTACK', fightSaga);
}

// Watcher pour la régénération de mana lors des déplacements
export function* watchMovement() {
    yield takeLatest('MOVE_PLAYER', function*() {
        const { hero } = yield select(state => state.gameData);
        if (hero) {
            yield* regenMana(hero);
        }
    });
}

// Fonction principale de la boucle de combat
function* fightSaga(action) {
    // Vérifier que l'action est valide
    if (!action || typeof action !== 'object') {
        console.error('Action invalide dans fightSaga:', action);
        return;
    }
    
    let isHeroAlive = true;
    let areMonstersAlive = true;
    const isSpecialAttack = action.type === 'FIGHT_SPECIAL_ATTACK';
    
    // Récupérer le héros et vérifier les données
    const hero = yield select(getHero);
    if (!hero || !hero.stats) {
        console.error('Héros non initialisé dans fightSaga:', hero);
        return;
    }
    
    // Vérifier le mana si attaque spéciale
    if (isSpecialAttack && !hasEnoughMana(hero, true)) {
        yield put(logsActions.add("Pas assez de mana pour une attaque spéciale !"));
        return;
    }

    const startingFightMonsters = yield select(getFightDataMonsters);

    // Une boucle représente un round du combat
    while (isHeroAlive && areMonstersAlive) {
        // Récupération des données du combat
        let monsters = yield select(getFightDataMonsters);
        const heroName = yield select(getHeroName);

        // Tour du héros ------------------------------------------------------

        yield delay(logDelay)
        yield put(logsActions.add("C'est au tour de " + heroName + " de frapper."));
        
        const { heroAttack, heroSpecialAttack, heroEscape, heroInventory } = yield race({
            heroAttack: take(GAME_DATA_ACTIONS.FIGHT_DATA.IS_DATA_PLAYER_ATTACK),
            heroSpecialAttack: take(GAME_DATA_ACTIONS.FIGHT_DATA.IS_DATA_PLAYER_SPECIAL_ATTACK),
            heroEscape: take(GAME_DATA_ACTIONS.FIGHT_DATA.IS_DATA_PLAYER_ESCAPE),
            heroInventory: take(GAME_DATA_ACTIONS.FIGHT_DATA.IS_DATA_PLAYER_INVENTORY),
        });
        
        if (heroAttack || heroSpecialAttack) {
            const isSpecialAttack = !!heroSpecialAttack;
            // Special action creator wraps payload as { specialAttackData: data }; basic uses { attackData: data }.
            const attackData = isSpecialAttack
                ? heroSpecialAttack.payload.specialAttackData
                : heroAttack.payload.attackData;
            const monstersIndex = yield select(getCurrentMonstersIndex);
            let damageDealt = 0;
            const hero = yield select(getHero);
            
            // Vérifier le coût en mana pour les attaques
            if (isSpecialAttack) {
                const manaCost = attackData.manaCost || MANA_COST.SPECIAL_ATTACK;
                if (!hasEnoughMana(hero, true)) {
                    yield put(logsActions.add(`Pas assez de mana pour cette attaque spéciale (${manaCost} MP requis) !`));
                    continue;
                }
                // Consommer le mana pour l'attaque spéciale
                yield* consumeMana(hero, true);
                yield put(logsActions.add(`Vous utilisez ${manaCost} points de mana pour votre attaque spéciale.`));
                
                // Gestion des attaques spéciales (pure helper, hero is plain data)
                const specialAttackResult = getSpecialAttack(hero);
                
                // Afficher le message de l'attaque spéciale
                yield put(logsActions.add(specialAttackResult.message));
                
                // Appliquer les effets spéciaux
                switch(attackData.type) {
                    case 'POISON':
                        // Appliquer l'effet de poison au monstre
                        const poisonDamage = Math.floor(getEffectiveStats(hero).strength * 0.3); // 30% des dégâts d'attaque
                        const poisonEffect = {
                            type: 'POISON',
                            damage: poisonDamage,
                            duration: 3,
                            remainingTurns: 3
                        };
                        
                        // Appliquer les dégâts initiaux
                        yield put(gameDataActions.fightData.monsterTakeDamageById(monstersIndex, poisonDamage));
                        damageDealt = poisonDamage;
                        
                        // Ajouter l'effet de poison au monstre
                        yield put({
                            type: 'FIGHT_DATA/MONSTER/APPLY_EFFECT',
                            payload: {
                                monsterIndex: monstersIndex,
                                effect: poisonEffect
                            }
                        });
                        
                        yield put(logsActions.add(`Le poison inflige ${poisonDamage} points de dégâts !`));
                        break;
                        
                    case 'STUN':
                        // Appliquer l'étourdissement au monstre
                        const stunChance = 75; // 75% de chance d'étourdir
                        if (Math.random() * 100 <= stunChance) {
                            const stunEffect = {
                                type: 'STUN',
                                duration: 1,
                                remainingTurns: 1
                            };
                            
                            yield put({
                                type: 'FIGHT_DATA/MONSTER/APPLY_EFFECT',
                                payload: {
                                    monsterIndex: monstersIndex,
                                    effect: stunEffect
                                }
                            });
                            
                            yield put(logsActions.add("Le monstre est étourdi et saute son prochain tour !"));
                        } else {
                            yield put(logsActions.add("L'attaque spéciale a échoué !"));
                        }
                        
                        // Infliger quand même des dégâts normaux (80 % du base)
                        damageDealt = calculateStunDamage(hero);
                        yield put(gameDataActions.fightData.monsterTakeDamageById(monstersIndex, damageDealt));
                        
                        // Traiter les effets des monstres après l'attaque
                        yield put({ type: 'FIGHT_DATA/MONSTER/PROCESS_EFFECTS' });
                        break;
                        
                    case 'ARMOR_PENETRATION': {
                        const result = calculateArmorPenDamage(hero);
                        damageDealt = result.damage;
                        yield put(gameDataActions.fightData.monsterTakeDamageById(monstersIndex, damageDealt));
                        if (result.isCritical) {
                            yield put(logsActions.add("Coup critique !"));
                        }
                        break;
                    }
                    
                    case 'DEFENSE_UP':
                        // Le Tank se protège — vitality est notre stat défensif
                        // (pas de champ `defense` séparé sur le héros pour l'instant).
                        const defenseBoost = Math.floor((getEffectiveStats(hero).vitality || 0) * 0.5);
                        
                        // Créer un effet de défense
                        const defenseEffect = {
                            type: 'DEFENSE_UP',
                            amount: defenseBoost,
                            duration: 2,
                            remainingTurns: 2
                        };
                        
                        // Appliquer l'effet au héros
                        yield put(heroActions.applyEffect(defenseEffect));
                        
                        // Afficher un message
                        yield put(logsActions.add(`${hero.name} augmente sa défense de ${defenseBoost} points pour 2 tours !`));
                        break;
                        
                    default:
                        log.saga.warn(`Type d'attaque spéciale non reconnu: ${attackData.type}`);
                        // Par défaut, faire une attaque normale
                        damageDealt = getEffectiveStats(hero).strength;
                        yield put(gameDataActions.fightData.monsterTakeDamageById(monstersIndex, damageDealt));
                        break;
                }
            } else {
                // Attaque normale
                const manaCost = attackData.manaCost || MANA_COST.BASIC_ATTACK;
                if (!hasEnoughMana(hero, false)) {
                    yield put(logsActions.add(`Pas assez de mana pour attaquer (${manaCost} MP requis) !`));
                    continue;
                }
                
                // Consommer le mana pour l'attaque normale
                yield* consumeMana(hero, false);
                yield put(logsActions.add(`Vous utilisez ${manaCost} points de mana pour votre attaque.`));
                
                const damage = calculateBasicDamage(hero, monsters[monstersIndex]);
                yield put(gameDataActions.fightData.monsterTakeDamageById(monstersIndex, damage));
                yield put(logsActions.add(`Vous infligez ${damage} points de dégâts au monstre !`));
                damageDealt = damage;
            }
            
            // Afficher les dégâts infligés
            if (damageDealt > 0) {
                const currentMonsters = yield select(getFightDataMonsters);
                const monsterName = currentMonsters[monstersIndex]?.name || 'le monste';
                yield put(logsActions.add(`${heroName} inflige ${damageDealt} points de dégâts à ${monsterName}`));
            }
            
            // Vérifier si le monstre est mort
            const updatedMonsters = yield select(getFightDataMonsters);
            if (updatedMonsters[monstersIndex]?.stats.health <= 0) {
                // Hold the death frame ~600 ms so the GSAP fade-out in
                // FightDisplayBox.MonsterCard can play before the card unmounts.
                yield delay(600);
                yield put(gameDataActions.fightData.removeMonsterByIndex(monstersIndex));
                
                // Vérifier si tous les monstres sont morts
                const remainingMonsters = yield select(getFightDataMonsters);
                if (remainingMonsters.length === 0) {
                    let amountExp = 0;
                    let amountGold = 0;

                    for (let index = 0; index < startingFightMonsters.length; index++) {
                        const monster = startingFightMonsters[index];
                        amountExp += monster.expDrop;
                        amountGold += 5;
                    }
                    
                    yield put(gameDataActions.fightData.setEndFightData(amountExp, amountGold));
                    return FIGHT_SAGA_RESULTS.PLAYER_VICTORY;
                } else {
                    yield put(logsActions.add("Un monstre a été vaincu !"));
                }
            }
        }

        if (heroEscape) {
            return FIGHT_SAGA_RESULTS.PLAYER_ESCAPE
        }

        if (heroInventory) {
            log.saga.log(heroInventory)
        }

        // Vérifier si un ou plusieurs monstres sont morts et les "sortir" du combat

        // Si tout les monstres sont morts -> fin du combat sinon tour des monstres


        // --------------------------------------------------------------------
        // Tour des monstres --------------------------------------------------
        yield put(logsActions.add("C'est au tour des monstres de frapper."));
        // Tour d'attaques des monstres
        
        for (let index = 0; index < monsters.length; index++) {
            const monster = monsters[index];
            // Déterminer les dégâts de l'attaque du monstre
            const damageRatio = 1 + ((monster.level - 1) * 0.1);
            const damage = monster.attack.damage * damageRatio;

            // Annoncer l'attaque du monstre
            yield delay(logDelay);
            yield put(logsActions.add(`${monster.name} va attaquer avec ${monster.attack.name}`));
            
            // Donner les dégâts au joueur et annoncer le résultat
            yield delay(logDelay);
            yield put(heroActions.takeDamage(damage));
            yield put(animateActions.switchHeroStatsDisplayPlayerImgDamage());
            yield put(logsActions.add(`${heroName} a perdu ${damage} HP`));

            // Vérifier si le joueur est toujours en vie
            const hero = yield select(getHero);
            if (hero.stats.health <= 0) {
                return FIGHT_SAGA_RESULTS.PLAYER_DEAD;
            }
        }
        
        // Vérifier si tous les monstres sont morts
        monsters = yield select(getFightDataMonsters);
        if (monsters.length === 0) {
            let amountExp = 0;
            let amountGold = 0;

            for (let index = 0; index < startingFightMonsters.length; index++) {
                const monster = startingFightMonsters[index];
                amountExp += monster.expDrop;
                amountGold += 5;
            }
            
            yield put(gameDataActions.fightData.setEndFightData(amountExp, amountGold));
            return FIGHT_SAGA_RESULTS.PLAYER_VICTORY;
        }
    }

    return false;
}

export default fightSaga;